import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Files.css';

// Small client-side file storage backed by IndexedDB. Holds invoices and other
// small documents. Capped so a workspace can't fill the browser. When the
// backend ships, this moves to server-side per-organization storage.
const STORAGE_CAP = 500 * 1024 * 1024; // 500 MB
const DB_NAME = 'ryzenos_files';
const STORE = 'files';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function dbAll() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
    tx.onsuccess = () => resolve(tx.result || []);
    tx.onerror = () => reject(tx.error);
  });
}
async function dbPut(rec) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite').objectStore(STORE).put(rec);
    tx.onsuccess = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function dbDel(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite').objectStore(STORE).delete(id);
    tx.onsuccess = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

const fmtSize = (b) => b < 1024 ? b + ' B' : b < 1048576 ? (b/1024).toFixed(1) + ' KB' : (b/1048576).toFixed(2) + ' MB';
const fmtDate = (t) => new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const extOf = (name) => (name.split('.').pop() || '').toUpperCase().slice(0, 4);

export default function Files() {
  const { theme } = useTheme();
  const fileRef = useRef();
  const [files, setFiles] = useState([]);
  const [msg, setMsg] = useState('');
  const [drag, setDrag] = useState(false);
  const [folder, setFolder] = useState('All');

  const refresh = () => dbAll().then(rows => setFiles(rows.sort((a, b) => b.date - a.date))).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const used = files.reduce((s, f) => s + (f.size || 0), 0);
  const pct = Math.min(100, Math.round(used / STORAGE_CAP * 100));
  const folders = ['All', ...Array.from(new Set(files.map(f => f.folder || 'General')))];
  const shown = folder === 'All' ? files : files.filter(f => (f.folder || 'General') === folder);

  const addFiles = async (list) => {
    let running = used;
    for (const file of list) {
      if (running + file.size > STORAGE_CAP) {
        setMsg(`Not enough space for "${file.name}". Free up room or delete older files.`);
        break;
      }
      const rec = { id: Date.now() + '-' + Math.random().toString(36).slice(2, 7), name: file.name, type: file.type, size: file.size, date: Date.now(), blob: file };
      await dbPut(rec);
      running += file.size;
    }
    refresh();
    setTimeout(() => setMsg(''), 4000);
  };

  const onPick = (e) => { if (e.target.files?.length) addFiles(Array.from(e.target.files)); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files)); };

  const download = (f) => {
    const url = URL.createObjectURL(f.blob);
    const a = document.createElement('a');
    a.href = url; a.download = f.name; a.click();
    URL.revokeObjectURL(url);
  };
  const remove = async (id) => { await dbDel(id); refresh(); };

  return (
    <div className="fl-wrap" data-theme={theme}>
      <Sidebar />
      <div className="fl-main">
        <Topbar title="Files" subtitle={`${files.length} file${files.length!==1?'s':''} · ${fmtSize(used)} used`} />
       <div className="fl-content">

        <div className="fl-meter-card">
          <div className="fl-meter-top">
            <span className="fl-meter-lbl">Storage</span>
            <span className="fl-meter-val">{fmtSize(used)} of {fmtSize(STORAGE_CAP)}</span>
          </div>
          <div className="fl-meter-bar"><div className="fl-meter-fill" style={{ width: pct + '%', background: pct > 90 ? '#EF4444' : '#FF6600' }} /></div>
        </div>

        <div className={`fl-drop${drag ? ' fl-drop--on' : ''}`}
          onClick={() => fileRef.current.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}>
          <div className="fl-drop-title">Drop files here or click to upload</div>
          <div className="fl-drop-hint">Invoices, documents and other small files. Up to {fmtSize(STORAGE_CAP)} total.</div>
          <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={onPick} />
        </div>

        {msg && <div className="fl-msg">{msg}</div>}

        {folders.length > 1 && (
          <div style={{display:'flex',gap:6,flexWrap:'wrap',margin:'4px 0 12px'}}>
            {folders.map(fd => (
              <button key={fd} type="button" onClick={()=>setFolder(fd)}
                style={{padding:'5px 12px',borderRadius:7,cursor:'pointer',fontFamily:'inherit',fontWeight:600,fontSize:12.5,
                  border:'1px solid '+(folder===fd?'#ff6600':'rgba(128,128,128,.3)'),
                  background: folder===fd?'rgba(255,102,0,.1)':'transparent',
                  color: folder===fd?'#ff6600':'inherit'}}>
                {fd}{fd!=='All' ? ` (${files.filter(f=>(f.folder||'General')===fd).length})` : ''}
              </button>
            ))}
          </div>
        )}

        {shown.length === 0 ? (
          <div className="fl-empty">No files yet. Upload your first document above.</div>
        ) : (
          <div className="fl-list">
            {shown.map(f => (
              <div key={f.id} className="fl-row">
                <div className="fl-ext">{extOf(f.name)}</div>
                <div className="fl-info">
                  <div className="fl-name" title={f.name}>{f.name}</div>
                  <div className="fl-meta">{fmtSize(f.size)} · {fmtDate(f.date)}</div>
                </div>
                <div className="fl-actions">
                  <button className="fl-btn" onClick={() => download(f)}>Download</button>
                  <button className="fl-btn fl-btn--del" onClick={() => remove(f.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
       </div>
      </div>
    </div>
  );
}
